import { useParams } from 'react-router-dom';
import { TFunction } from 'i18next';

import { createHandle, createLoader } from 'services/breadcrumbs/breadcrumbs';
import { useMapVirtualToActualDocumentType } from 'services/documentTypes/documentTypes';
import { useRelationship } from 'services/repositories/relationships/relationships';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { DocumentTypeWithVirtual, PriceCatalogRoutes, TransactionsRoutes } from 'support/types';

import { DocumentCreation } from '../components/CreateDocument/CreateDocument';

type RouteParams = {
  relationshipId: string;
  messageType: DocumentTypeWithVirtual;
};

export const CreateDocumentStandalone = ({ isProductCatalogRoute }: { isProductCatalogRoute: boolean }) => {
  const { relationshipId, messageType: virtualDocumentType } = useParams<RouteParams>();
  const { data: relationship } = useRelationship({
    variables: {
      relationshipId: relationshipId,
    },
    enabled: !!relationshipId,
  });
  const documentType = useMapVirtualToActualDocumentType(virtualDocumentType);

  return (
    <DocumentCreation
      sourceMessageId={undefined}
      relationship={relationship}
      isProductCatalogRoute={isProductCatalogRoute}
      virtualDocumentType={virtualDocumentType}
      documentType={documentType}
    />
  );
};

CreateDocumentStandalone.loader = createLoader(
  ({ params }) => useRelationship.getKey({ relationshipId: params.relationshipId }),
  useRelationship.queryFn,
);

type ResponseType = Awaited<ReturnType<typeof useRelationship.queryFn>>;
CreateDocumentStandalone.handle = createHandle<ResponseType, RouteParams>((params, data) => ({
  label: (t: TFunction) =>
    t('webedi:createDocument.breadcrumb', {
      documentType: t(`common:messageTypes.singular.${data?.message_type || 'UNKNOWN'}`),
    }),
  to: routeToPage(
    params.messageType === DocumentTypeWithVirtual.productCatalog
      ? PriceCatalogRoutes.create
      : TransactionsRoutes.createStandalone,
    {
      relationshipId: params.relationshipId,
      messageType: params.messageType,
    },
  ),
  key: `createDocument`,
}));
